import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { schedulingInsightsRoute } from 'api';
import { fetchJSON } from 'api/fetch';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import {
  SchedulingInsightsEvent,
  SchedulingInsightsEventsState,
} from 'features/scheduleBuilder/slices/schedulingInsightsEvents/types';

import { datesInRange } from 'util/dateTime';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import { getEvents } from './helper';

type currentDateRangeProps = {
  start: string;
  end: string;
};

export const fetchSchedulingInsightsEvents = createAsyncThunk(
  'scheudlingInsightsEvents/fetchSchedulingInsightsEvents',
  async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
    try {
      const response = await Promise.race([
        fetchJSON(schedulingInsightsRoute({ startDate, endDate })),
      ]);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getScheduleBuilderState = (state: RootState) =>
  state.get(PARENT_SLICE_NAME);

const getSchedulingInsightsEventsState = (state: RootState) =>
  getScheduleBuilderState(state)?.schedulingInsightsEvents || {
    ids: [],
    entities: {},
  };

const SchedulingInsightsEventsAdapter =
  createEntityAdapter<SchedulingInsightsEvent>({
    selectId: schedulingInsightsEvent =>
      `${schedulingInsightsEvent.event_id}-${schedulingInsightsEvent.event_start}`,
  });

export const initialState: SchedulingInsightsEventsState =
  SchedulingInsightsEventsAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: state => {
      SchedulingInsightsEventsAdapter.removeAll(state);
      state.status = 'idle';
      state.error = undefined;
    },
  },
  extraReducers: builder => {
    builder
      // Handle pending state
      .addCase(fetchSchedulingInsightsEvents.pending, state => {
        state.status = 'pending';
      })
      // Handle successful fetch
      .addCase(fetchSchedulingInsightsEvents.fulfilled, (state, action) => {
        const events = Array.isArray(action.payload) ? action.payload : [];

        SchedulingInsightsEventsAdapter.setAll(state, events);
        state.status = 'succeeded';
      })
      // Handle fetch failure
      .addCase(fetchSchedulingInsightsEvents.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch recommendations';
        state.status = 'failed';
      });
  },
});

export const selectSchedulingInsightsEvents = createCachedSelector(
  (state: RootState) => getSchedulingInsightsEventsState(state),
  (_: RootState, props: currentDateRangeProps) => props,
  (schedulingInsightsEventsState, props) => {
    const dates = datesInRange(props.start, props.end);

    const events = Object.values(schedulingInsightsEventsState.entities)
      .filter((event): event is SchedulingInsightsEvent => event != null)
      .map(event => getEvents(dates, event))
      .flat();

    // Group events by date
    const eventsByDate: Record<string, SchedulingInsightsEvent[]> = {};

    events.forEach(event => {
      const eventDate = event.date;
      if (!eventsByDate[eventDate]) {
        eventsByDate[eventDate] = [];
      }
      eventsByDate[eventDate].push(event);
    });

    return eventsByDate;
  }
)(
  (_: RootState, props) =>
    `selectPredictedEventsonDate-${props.start}-${props.end}`
);
export const { reducer } = slice;
